import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Avatar, Button, Layout, Menu, message, Badge, Popover } from 'antd';
import {
  FolderOpenOutlined,
  LogoutOutlined,
  MailOutlined,
  UserOutlined
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import {
  outOfNavRoutes,
  subRoutes,
  routes
} from '../../../utils/constants/routes';
import useAuthContext from '../../../contexts/AuthContext';
import HeaderDropdown from '../../HeaderDropdown/HeaderDropdown';
import Logo from '../../../assets/images/logo-assurmax.png';
import LogoCollapsed from '../../../assets/images/logo-assurmax-collapsed.png';
import BackButton from '../../BackButton/BackButton';
import useAssurmaxContext from '../../../contexts/AssurmaxContext';
import AdvancedSearch from '../../AdvancedSearch/AdvancedSearch';

const { Header } = Layout;

const StyledHeader = styled.div`
  background: #fff;
  box-shadow: 0 1px 4px rgba(0, 21, 41, 0.08);
  padding: 0;
  z-index: 8;
  position: fixed;
  height: 70px;
  width: auto;
  left: ${({ collapsed }) => (collapsed ? '81px' : '257px')};
  display: flex;
  flex-wrap: no-wrap;
  align-items: center;

  @media (max-width: 992px) {
    width: auto;
    left: 80px;
    right: 80px;
  }

  @media (max-width: 576px) {
    left: 0px;
  }

  div:last-child a {
    color: var(--textColor);
  }
`;

const StyledContainer = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  height: 100%;
  overflow: hidden;
`;

const StyledDropdownContainer = styled.span`
  height: 100%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-items: center;
  padding: 0 12px;
  transition: all 0.3s;
  :hover {
    background: var(--contrastBackground);
  }
`;

const StyledFullNameSpan = styled.span`
  color: var(--textColor);
  font-weight: bold;
`;

const StyledRoleP = styled.p`
  font-size: 10px;
  color: var(--textColor);
`;

const LogoDiv = styled.div`
  justify-self: center;
  margin: auto;
  max-width: 180px;
  height: 64px;
  overflow: hidden;
  line-height: 64px;
  transition: all 0.3s;
  z-index: 900;
  display: flex;
  justify-content: center;

  @media (max-width: 992px) {
    width: 50px;
  }

  img {
    display: inline-block;
    height: 48px;
    vertical-align: middle;
  }
`;

const iconStyle = {
  fontSize: 16
};

const HeaderLayout = ({ leftCollapsed, rightCollapsed, themeLogo, isACC }) => {
  const navigate = useNavigate();
  const { PORTFOLIO_AMENDMENTS_IN_EXEMPTION, QUOTES_IN_EXEMPTION } =
    useAssurmaxContext();
  const { t } = useTranslation();
  const { dispatchAPI, user } = useAuthContext();
  const [color, setColor] = useState(false);

  const total =
    PORTFOLIO_AMENDMENTS_IN_EXEMPTION || 0 + QUOTES_IN_EXEMPTION || 0;

  useEffect(() => {
    const timer = setInterval(() => {
      setColor((prev) => !prev);
    }, 1000);
    return () => clearInterval(timer);
    // eslint-disable-next-line
  }, []);

  const onProfileMenuClick = ({ key }) => {
    if (key === 'logout') {
      dispatchAPI('LOGOUT');
      message.success(t('login.logoutSuccess'));
    }
  };

  const profileMenu = (
    <Menu onClick={onProfileMenuClick}>
      {window.innerWidth <= 992 && user && (
        <Menu.Item key="user">
          <Button
            style={{
              width: '100%',
              height: 50
            }}
            type="link"
          >
            <StyledFullNameSpan>
              {`${user.first_name} ${user.last_name}`}
            </StyledFullNameSpan>
            <StyledRoleP>
              {t(`users.tags.${user.role.split(':')[1]}`)}
            </StyledRoleP>
          </Button>
        </Menu.Item>
      )}
      <Menu.Item key="profile" onClick={() => navigate(outOfNavRoutes.PROFILE)}>
        <UserOutlined style={iconStyle} />
        {` ${t('header.menu.user.profile')}`}
      </Menu.Item>
      <Menu.Divider />
      <Menu.Item key="logout">
        <LogoutOutlined style={iconStyle} />
        {` ${t('header.menu.user.logout')}`}
      </Menu.Item>
    </Menu>
  );

  const isSuperUser = user?.role === 'users:SUPER-USER';

  const getRightPosition = () => {
    if (isSuperUser) {
      if (rightCollapsed) return 80;
      return 200;
    }
    return 0;
  };

  return (
    <StyledHeader
      as={Header}
      collapsed={leftCollapsed ? 1 : 0}
      style={{
        right: getRightPosition()
      }}
    >
      {themeLogo && (
        <img
          style={{
            width: 100,
            maxHeight: 50,
            position: 'absolute',
            top: 10,
            left: '50%',
            marginLeft: '-50px'
          }}
          src={themeLogo}
          alt="Company logo"
        />
      )}
      <BackButton
        style={{
          justifySelf: 'start'
        }}
      />
      <LogoDiv>
        <Link to="/">
          <img alt="Logo" src={leftCollapsed ? LogoCollapsed : Logo} />
        </Link>
      </LogoDiv>
      {!isACC && (
        <StyledContainer>
          <HeaderDropdown overlayMenu={profileMenu}>
            {window.innerWidth <= 992 ? (
              <StyledDropdownContainer>
                <UserOutlined />
              </StyledDropdownContainer>
            ) : (
              <StyledDropdownContainer>
                <Avatar
                  size="medium"
                  icon={user?.photo ? '' : <UserOutlined />}
                  src={user?.photo ? user.photo : ''}
                >
                  {user && `${user.first_name[0]}${user.last_name[0]}`}
                </Avatar>
                <Button type="link" style={{ marginLeft: 12, height: 44 }}>
                  <StyledFullNameSpan>
                    {`${user?.first_name} ${user?.last_name}`}
                  </StyledFullNameSpan>
                  <StyledRoleP>
                    {t(`users.tags.${user?.role.split(':')[1]}`)}
                  </StyledRoleP>
                </Button>
              </StyledDropdownContainer>
            )}
          </HeaderDropdown>
        </StyledContainer>
      )}
      <div style={{ marginRight: isACC ? 30 : 230, display: 'flex' }}>
        {total !== 0 ? (
          <Popover placement="bottom" content={t('basiclayout.menu.tickets')}>
            <Link
              to={
                QUOTES_IN_EXEMPTION === 0
                  ? `${routes.CONTRACTS}${subRoutes.CONTRACTS.PORTFOLIO_AMENDMENTS}`
                  : `${routes.QUOTES}${subRoutes.QUOTES.QUOTES_IN_EXEMPTION}`
              }
            >
              <FolderOpenOutlined
                style={{
                  fontSize: 27,
                  marginRight: 10,
                  color: color && 'var(--secondaryColor)'
                }}
              />
              <Badge
                offset={[-12, -26]}
                count={total}
                style={{ background: 'var(--secondaryColor)' }}
              />
            </Link>
          </Popover>
        ) : (
          []
        )}
        <Popover placement="bottom" content={t('basiclayout.menu.messages')}>
          <a href="mailto:courtiers@assurmaxpro.com">
            <MailOutlined style={{ fontSize: 25 }} />
            <Badge
              offset={[-7, -26]}
              style={{ background: 'var(--secondaryColor)' }}
            />
          </a>
        </Popover>
        {user?.role !== 'guests:GUEST' && <AdvancedSearch />}
      </div>
    </StyledHeader>
  );
};

HeaderLayout.propTypes = {
  leftCollapsed: PropTypes.bool.isRequired,
  rightCollapsed: PropTypes.bool.isRequired,
  themeLogo: PropTypes.string,
  isACC: PropTypes.bool.isRequired
};

HeaderLayout.defaultProps = {
  themeLogo: null
};

export default HeaderLayout;
